import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { grey } from '@mui/material/colors';
import { Box, Drawer, Grid, ListItemButton, Typography } from '@mui/material';

import { THEMES } from '../constants';
import useTheme from '../hooks/useTheme';
import { useAppShellStore } from '../stores';

type ThemeButtonType = {
  active?: boolean;
};

const ThemeButton = styled.div<ThemeButtonType>`
  cursor: pointer;
  background: ${(props) => props.theme.palette.background.paper};
  height: 80px;
  border-radius: 0.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.825rem;
  position: relative;
  border: 1px solid ${(props) => (!props.active ? props.theme.palette.action.selected : props.theme.palette.action.active)};
`;

type ThemeButtonInnerType = {
  selectedTheme: string;
};

const ThemeButtonInner = styled.div<ThemeButtonInnerType>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px ${(props) => props.theme.palette.action.selected};
  position: relative;

  ${(props) =>
    props.selectedTheme === THEMES.DEFAULT &&
    css`
      background: linear-gradient(-45deg, #23303f 50%, ${grey[100]} 0);
    `}
  ${(props) =>
    props.selectedTheme === THEMES.DARK &&
    css`
      background: #23303f;
    `}
  ${(props) =>
    props.selectedTheme === THEMES.LIGHT &&
    css`
      background: ${grey[100]};
    `}
  ${(props) =>
    props.selectedTheme === THEMES.BLUE &&
    css`
      background: linear-gradient(-45deg, #4782da 50%, ${grey[100]} 0);
    `}
`;

const ThemesTitle = styled(Typography)`
  text-align: center;
`;

const Wrapper = styled.div`
  width: 300px;
  overflow-x: hidden;
`;

const Heading = styled(ListItemButton)`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding: 0 ${(props) => props.theme.spacing(6)};

  ${(props) => props.theme.breakpoints.up('sm')} {
    min-height: 64px;
  }
`;

type ThemeTypes = {
  title: string;
  themeVariant: string;
};

function ThemeItem({ title, themeVariant }: ThemeTypes) {
  const { theme, setTheme } = useTheme();

  return (
    <Grid item xs={6}>
      <ThemeButton active={themeVariant === theme} onClick={() => setTheme(themeVariant)}>
        <ThemeButtonInner selectedTheme={themeVariant} />
      </ThemeButton>
      <ThemesTitle variant="subtitle2" gutterBottom>
        {title}
      </ThemesTitle>
    </Grid>
  );
}

function ThemesDisplay() {
  return (
    <Wrapper>
      <Heading>Theme Selector</Heading>
      <Box px={6} my={3}>
        <Grid container spacing={3}>
          <ThemeItem title="Blue" themeVariant={THEMES.BLUE} />
          <ThemeItem title="Dark" themeVariant={THEMES.DARK} />
          <ThemeItem title="Light" themeVariant={THEMES.LIGHT} />
          <ThemeItem title="Light/Dark" themeVariant={THEMES.DEFAULT} />
        </Grid>
      </Box>
    </Wrapper>
  );
}

function ThemePicker() {
  const isThemeDrawerOpen = useAppShellStore.use.isThemeDrawerOpen();
  const toggleThemeDrawer = useAppShellStore.use.toggleThemeDrawer();

  return (
    <React.Fragment>
      <Drawer anchor="right" open={isThemeDrawerOpen} onClose={() => toggleThemeDrawer(false)}>
        <ThemesDisplay />
      </Drawer>
    </React.Fragment>
  );
}

export default ThemePicker;
